import { Comment } from '@/models/CommentModel';
import { client } from './API';
import { Card, CardAttachment } from '@/models/card/CardModel';
import { ENVIRONMENTAL_VARIABLES } from '@/utils/Environment';

/**
 * Fetch list of deliveries
 */
export const fetchDeliveries = ({
  communityId,
  roomId,
  boardId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
}): Promise<Card[]> => {
  return client
    .get(`${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/deliveries`)
    .then((response) => response.data);
};

/**
 * Create delivery
 */
export const createDelivery = ({
  communityId,
  roomId,
  boardId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  data: Card;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/deliveries`,
      data,
    )
    .then((response) => response.data);
};

/**
 * Update delivery values
 */
export const updateDelivery = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  data,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  data: Card;
}): Promise<Card> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/deliveries/${deliveryId}`,
      data,
    )
    .then((response) => response.data);
};

/**
 * Delete delivery
 */
export const deleteDelivery = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
}): Promise<Card> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/deliveries/${deliveryId}`,
    )
    .then((response) => response.data);
};

/**
 * Fetch delivery attachment infos
 */
export const fetchDeliveryAttachments = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
}): Promise<CardAttachment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/attachments`,
    )
    .then((response) => response.data);
};

/**
 * Upload attachment with content to delivery
 */
export const uploadDeliveryAttachment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  file,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  file: File;
}): Promise<CardAttachment> => {
  const data = new FormData();
  data.append('file', file, file.name);
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/files`,
      data,
      { headers: { Accept: '*/*', 'Content-Type': 'multipart/form-data' } },
    )
    .then((response) => response.data);
};

/**
 * Add link to delivery
 */
export const addDeliveryLink = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  link,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  link: any;
}): Promise<Card> => {
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/links`,
      link,
    )
    .then((response) => response.data);
};

/**
 * Delete attachment from delivery
 */
export const deleteDeliveryAttachment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  attachmentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  attachmentId: string;
}): Promise<Card> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/attachments/${attachmentId}`,
    )
    .then((response) => response.data);
};

export const downloadDeliveryAttachment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  attachmentId,
  version,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  attachmentId: string;
  version: string;
}): Promise<Blob> => {
  const config = {
    responseType: 'blob', // important
    params: version ? { version } : {},
  };
  return client.get(
    `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/files/${attachmentId}`,
    config as any,
  );
};

/**
 * Fetch all delivery comments
 */
export const fetchDeliveryComments = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
}): Promise<Comment[]> => {
  return client
    .get(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/comments`,
    )
    .then((response) => response.data);
};

/**
 * Create comment in delivery
 */
export const createDeliveryComment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  content,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  content: string;
}): Promise<Comment> => {
  const comment = { content };
  return client
    .post(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/comments`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Update comment in deliveries
 */
export const updateDeliveryComment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  comment,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  comment: Comment;
}): Promise<Comment> => {
  return client
    .put(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/comments/${comment.id}`,
      comment,
    )
    .then((response) => response.data);
};

/**
 * Delete comment in deliveries
 */
export const deleteDeliveryComment = ({
  communityId,
  roomId,
  boardId,
  deliveryId,
  commentId,
}: {
  communityId: string;
  roomId: string;
  boardId: string;
  deliveryId: string;
  commentId: string;
}): Promise<Comment> => {
  return client
    .delete(
      `${ENVIRONMENTAL_VARIABLES.API_ALFRESCO_URL}/${communityId}/rooms/${roomId}/boards/${boardId}/cards/${deliveryId}/comments/${commentId}`,
    )
    .then((response) => response.data);
};
